import { Api } from "../services";

export const GET_STUDENT_STATS = 'GET_STUDENT_STATS';
export const GET_STUDENT_LIST = 'GET_STUDENT_LIST';
export const GET_STUDENT_AUTOCOMPLETE = 'GET_STUDENT_AUTOCOMPLETE';
export const ADD_STUDENT_SUCCESS = 'ADD_STUDENT_SUCCESS';
export const GET_STUDENT_PROFILE = 'GET_STUDENT_PROFILE';

export const getStudentStatsSuccess = (data) => ({
    type: GET_STUDENT_STATS,
    data
});

export const getStudentListSuccess = (data) => ({
    type: GET_STUDENT_LIST,
    data
});

export const getStudentAutocompleteSuccess = (data) => ({
    type: GET_STUDENT_AUTOCOMPLETE,
    data
});

export const addStudentSuccess = (data) => ({
    type: ADD_STUDENT_SUCCESS,
    data
});

export const getStudentProfileSuccess = (data) => ({
    type: GET_STUDENT_PROFILE,
    data
});

// GET /userv2/student/stats — dashboard counts (total + byCourseType) and
// the enrollment trend used for ActiveStudentsCard's graph.
export const getStudentStats = (params, callback) => {
    return (dispatch, getState) => {
        const { skipRedux, ...queryParams } = params || {};
        // Params go in the URL: Api.send queues requests by URL, so the distribution
        // card's call and ActiveStudentsCard's (same path, different params) were
        // overwriting each other and the card kept showing old redux counts.
        const qs = new URLSearchParams(
            Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null && v !== '')
        ).toString();
        const url = `/userv2/student/stats${qs ? `?${qs}` : ''}`;
        // timed=true sends immediately (like getStaffStats) instead of the
        // default 1s debounce in Api.send, so filter/year changes show fast.
        return Api.get(url).send((response, error) => {
            if (!skipRedux) {
                if (response && response.data) {
                    dispatch(getStudentStatsSuccess(response.data));
                } else if (response) {
                    dispatch(getStudentStatsSuccess(response));
                }
            }
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// GET /api/userv2/student?standardId=<batchId>
// List students in a paginated, searchable, filterable dashboard table.
export const getStudentList = (params, callback) => {
    return (dispatch, getState) => {
        const query = {
            ...(params || {}),
            page: Number(params?.page || 1),
            limit: Number(params?.limit || 20),
        };

        const skipRedux = Boolean(query.skipRedux);
        delete query.skipRedux;

        if (typeof query.search === 'string' && !query.search.trim()) delete query.search;
        if (!query.courseType) delete query.courseType;
        if (query.courseType === 'live') query.courseType = 'course';
        if (!query.courseId) delete query.courseId;
        if (!query.standardId && query.batchId) {
            query.standardId = query.batchId;
        }
        delete query.batchId;
        if (!query.standardId) delete query.standardId;
        if (!query.status || query.status === 'All') delete query.status;

        // timed=true: sent immediately instead of Api.send's default 1s
        // debounce, so the list (e.g. right after adding a student) loads fast.
        return Api.get('/userv2/student').params(query).send((response, error) => {
            if (response && !error && !skipRedux) {
                dispatch(getStudentListSuccess(response));
            }
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// Helper action to fetch student list for a specific batch (standardId)
export const getStudentListByBatch = (standardId, params, callback) => {
    const extraParams = typeof params === 'function' ? {} : (params || {});
    const cb = typeof params === 'function' ? params : callback;
    return getStudentList({ ...extraParams, standardId }, cb);
};


// GET /api/userv2/student/autocomplete
// Search-as-you-type by name, returning the top 10 results.
export const getStudentAutocomplete = (params, callback) => {
    return (dispatch, getState) => {
        const query = { ...(params || {}) };
        if (typeof query.search !== 'string' || !query.search.trim()) {
            dispatch(getStudentAutocompleteSuccess([]));
            if (callback) callback([], null);
            return Promise.resolve([]);
        }

        return Api.get('/userv2/student/autocomplete').params(query).send((response, error) => {
            const payload = Array.isArray(response) ? response : response?.data ?? [];
            dispatch(getStudentAutocompleteSuccess(payload));
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /userv2/student/:userId — full profile (Basic Details tab fields plus
// quickStats/loginAndAccount/feeReminder sidebars) for the student profile page.
export const getStudentProfile = (userId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}`;
        // timed=true: sent immediately instead of Api.send's default 1s
        // debounce (profile reload after Save in Basic details).
        return Api.get(url).send((response, error) => {
            if (response && !error) {
                dispatch(getStudentProfileSuccess(response?.data ?? response));
            }
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// PATCH /userv2/student/:userId — edit profile fields (same field set as
// addStudent, all optional, including phone which re-triggers the
// duplicate-phone check server-side if changed). Response is the flattened
// profile object, without the sidebars.
export const updateStudentProfile = (userId, params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}`;
        // timed=true: sent immediately (no 1s Api.send debounce).
        return Api.patch(url).params(params).send((response, error) => {
            if (response && !error) {
                dispatch(getStudentProfileSuccess(response?.data ?? response));
            }
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// POST /userv2/student/:userId/photo — multipart upload/replace student
// profile photo (field name: file). Uses uploadAndPost (not the .send()
// queue) since this is multipart, not JSON — callback receives
// (percent, response), same as uploadStaffPhoto.
export const uploadStudentPhoto = (userId, file, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/photo`;
        return Api.uploadAndPost(url, { file }, callback);
    };
};

// DELETE /userv2/student/:userId/photo — remove student profile photo.
export const deleteStudentPhoto = (userId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/photo`;
        return Api.delete(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// POST /userv2/student/:userId/documents — multipart upload a new document
// to this student's profile (field name: file; pdf/doc/docx/jpg/jpeg/png,
// max 10MB). Response is the newly created Document row. Same uploadAndPost
// shape as uploadStudentPhoto/uploadStaffDocument — callback fires
// repeatedly with just a percent during upload, then finally with
// (100, response) on success or (-1) on failure.
export const uploadStudentDocument = (userId, file, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/documents`;
        return Api.uploadAndPost(url, { file }, callback);
    };
};

// GET /userv2/student/:userId/documents — every document uploaded for this
// student, newest first. No reducer wiring, same as getStaffDocuments — the
// caller (StaffProfilePage) keeps its own local state.
export const getStudentDocuments = (userId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/documents`;
        return Api.get(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// PATCH /userv2/student/:userId/documents/:documentId — replace the file
// behind an existing document row. Multipart, so this chains .patch(url)
// (which only sets the URL/method) into uploadAndPatch (which reads that
// URL) rather than .send() — same percent/(100,response)/(-1) callback
// shape as replaceStaffDocument.
export const replaceStudentDocument = (userId, documentId, file, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/documents/${documentId}`;
        return Api.patch(url).uploadAndPatch({ file }, callback);
    };
};

// DELETE /userv2/student/:userId/documents/:documentId — permanently
// deletes the document (file + row), cannot be undone. Response body is
// empty on success, so callers should only branch on `error`, not on
// response being truthy.
export const deleteStudentDocument = (userId, documentId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/documents/${documentId}`;
        return Api.delete(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// PATCH /student/:userId/status — single-student suspend/activate/delete.
// Real enum values only: "Approved" | "Suspended" | "Deleted" (there is no
// "Active" on the API side — "Approved" is what an activated student holds).
export const updateStudentStatus = (userId, status, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/status`;
        return Api.patch(url).params({ status }).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// PATCH /student/status — bulk suspend/delete (admin only), e.g. bulk
// delete/suspend via { userIds, status }. Response is partial-success
// shaped: { succeeded: number[], failed: [{ userId, reason }] } — the
// caller's own id, for example, fails while the rest still go through.
// status here is only "Suspended" | "Deleted" (not usable to re-approve).
export const bulkUpdateStudentStatus = (userIds, status, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/status`;
        return Api.patch(url).params({ userIds, status }).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// POST /student — add a student manually (personal info only: name/phone
// required, email optional). Course/batch enrollment is a separate
// POST /:userId/enrollments call, not part of this action.
export const addStudent = (params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student`;
        // timed=true: sent immediately (no 1s Api.send debounce).
        return Api.post(url).params(params).send((response, error) => {
            if (response && !error) {
                dispatch(addStudentSuccess(response));
            }
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// POST /userv2/student/:userId/enrollments — Enroll into N courses in one atomic call
// used by the “Add Student” screen’s repeatable enrollment blocks
export const enrollStudent = (userId, params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/enrollments`;
        // timed=true: sent immediately (no 1s Api.send debounce).
        return Api.post(url).params(params).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// GET /api/userv2/student/:userId/courses
// Retrieves a student's enrolled courses/batches with completion percentage.
export const getStudentCourses = (userId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/courses`;
        return Api.get(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// POST /api/userv2/student/:userId/courses {courseType, courseId, standardId?, rollNo?, enrollmentDate}
// Add one course/batch to an existing student
export const enrollStudentCourse = (userId, params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/courses`;
        return Api.post(url).params(params).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// DELETE /api/userv2/student/:userId/courses/:enrollmentId?courseType=
// Remove (soft-delete) one enrollment
export const unassignStudentCourse = (userId, enrollmentId, courseType, callback) => {
    return (dispatch, getState) => {
        let url = `/userv2/student/${userId}/courses/${enrollmentId}`;
        if (courseType) {
            url += `?courseType=${encodeURIComponent(courseType)}`;
        }
        return Api.delete(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// PATCH /api/userv2/student/:userId/courses/:enrollmentId/batch
// Change a student's batch/standard
export const updateStudentBatch = (userId, enrollmentId, params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/courses/${enrollmentId}/batch`;
        return Api.patch(url).params(params).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// POST /api/userv2/student/assign-standard
// Assign an additional standard (batch) to a student's course enrollment
export const assignStudentStandard = (params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/assign-standard`;
        return Api.post(url).params(params).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        }, true);
    };
};

// POST /api/userv2/student/unassign-standard
// Remove a student's standard assignment
export const unassignStudentStandard = (params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/unassign-standard`;
        return Api.post(url).params(params).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /api/student/courses?mode=online|hybrid|offline (mode optional)
// Retrieves course standards list with student and batch counts
export const getStudentCourseList = (params, callback) => {
    return (dispatch, getState) => {
        const query = { ...(params || {}) };
        if (!query.mode || String(query.mode).toLowerCase() === 'all') {
            delete query.mode;
        } else {
            query.mode = String(query.mode).toLowerCase();
        }
        return Api.get('/userv2/student/courses').params(query).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /api/student/courses/:courseId/batches
// Retrieves batches/standards for a specific course (pass -1 for no course batches; don't pass 0)
export const getStudentCourseBatches = (courseId, callback) => {
    return (dispatch, getState) => {
        let targetId = courseId;
        if (targetId === 0 || targetId === '0' || targetId === undefined || targetId === null) {
            targetId = -1;
        }
        const url = `/userv2/student/courses/${targetId}/batches`;
        return Api.get(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /api/userv2/student/resources?type=videoResource|studyMaterial|testSeries
export const getStudentResources = (params, callback) => {
    return (dispatch, getState) => {
        const query = { ...(params || {}) };
        return Api.get('/userv2/student/resources').params(query).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /api/attendance/student-attendance-tab/:studentId?courseId=&date=
export const getStudentAttendanceTab = (studentId, params, callback) => {
    return (dispatch, getState) => {
        const query = { ...(params || {}) };
        if (!query.courseId) delete query.courseId;
        if (!query.date) delete query.date;
        const url = `/attendance/student-attendance-tab/${studentId}`;
        return Api.get(url).params(query).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /attendance/teacher-attendance-tab/:teacherId?date=&year=
export const getTeacherAttendanceTab = (teacherId, params, callback) => {
    return (dispatch, getState) => {
        const query = { ...(params || {}) };
        if (!query.date) delete query.date;
        if (!query.year) delete query.year;
        const url = `/attendance/teacher-attendance-tab/${teacherId}`;
        return Api.get(url).params(query).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /attendance/operation-attendance-tab/:operatorId?date=&year=
export const getOperationAttendanceTab = (operatorId, params, callback) => {
    return (dispatch, getState) => {
        const query = { ...(params || {}) };
        if (!query.date) delete query.date;
        if (!query.year) delete query.year;
        const url = `/attendance/operation-attendance-tab/${operatorId}`;
        return Api.get(url).params(query).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /api/userv2/student/:userId/fee
export const getStudentFee = (userId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/fee`;
        return Api.get(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// POST /api/userv2/student/assign-course-bulk
// Body: { courseId: number, students: number[] }
export const assignCourseBulk = (params, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/assign-course-bulk`;
        return Api.post(url).params(params).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};

// GET /api/userv2/student/:userId/activity-stats
export const getStudentActivityStats = (userId, callback) => {
    return (dispatch, getState) => {
        const url = `/userv2/student/${userId}/activity-stats`;
        return Api.get(url).send((response, error) => {
            if (callback) {
                callback(response, error);
            }
        });
    };
};


